import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import Swal from "sweetalert2";
import swal from "sweetalert";
import { APILA } from "../../../../../config/apila";
import { APIMS } from "../../../../../config/apims";
import ToastError from "../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../NotificationToast/ToastSuccess";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";

import "bulma/css/bulma.css";
import "../../../../../index.css";

const pageStyles = `
  .kategori-penilai-confirm-popup {
    width: min(420px, calc(100vw - 32px)) !important;
    padding: 22px 20px 20px !important;
    border-radius: 12px !important;
    font-size: 13px !important;
  }
  .kategori-penilai-confirm-popup .swal2-icon {
    width: 58px !important;
    height: 58px !important;
    margin: 4px auto 14px !important;
    font-size: 13px !important;
  }
  .kategori-penilai-confirm-title {
    font-size: 20px !important;
    font-weight: 700 !important;
    line-height: 1.5 !important;
    padding: 0 !important;
  }
  .kategori-penilai-confirm-text {
    font-size: 14px !important;
    line-height: 1.6 !important;
    margin: 10px 0 0 !important;
    padding: 0 !important;
  }
  .kategori-penilai-confirm-actions {
    margin-top: 20px !important;
    gap: 8px !important;
  }
  .kategori-penilai-confirm-actions .swal2-styled {
    font-size: 13px !important;
    font-weight: 600 !important;
    padding: 9px 15px !important;
    border-radius: 7px !important;
    margin: 0 !important;
  }
  .kategori-penilai-sync-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 13px;
    background: #385deb;
    color: #fff;
    border: 0;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    font-size: 13px;
  }
  .kategori-penilai-sync-button:hover:not(:disabled) { background: #2c4dd1; }
  .kategori-penilai-sync-button:disabled { opacity: .55; cursor: not-allowed; }
  .kategori-penilai-sync-wrap { margin-left: auto; align-self: flex-end; }
  .kategori-penilai-toolbar { display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
  @media (max-width: 780px) {
    .kategori-penilai-sync-wrap { margin-left: 0; width: 100%; }
    .kategori-penilai-sync-button { width: 100%; }
  }
  .kategori-penilai-table {
    min-width: 620px;
  }

  .kategori-penilai-col-bobot {
    // width: 300px;
    text-align: center !important;
  }

  .kategori-penilai-col-action {
    width: 150px;
    text-align: center !important;
  }

  .kategori-penilai-action-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    white-space: nowrap;
  }

  @media (max-width: 780px) {
    .kategori-penilai-table {
      min-width: 560px;
    }
  }
`;

export default function KategoriPenilai() {
  document.title = "Kategori Penilai";

  const token = localStorage.getItem("token");
  const storageLevel = localStorage.getItem("level");

  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [semesterOptions, setSemesterOptions] = useState([]);
  const [activeAcademicYear, setActiveAcademicYear] = useState({
    id: "",
    name: "",
    activeSemesterId: "",
  });
  const [data, setData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");

  const [formFilter, setFormFilter] = useState({
    ysb_academic_year_id: "",
    ysb_semester_id: "",
  });

  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedData, setSelectedData] = useState(null);

  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const location = useLocation();
  const navigate = useNavigate();

  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  const firstLoadDone = useRef(false);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  useEffect(() => {
    const rolePermissions =
      JSON.parse(localStorage.getItem("role_permission")) || [];

    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({
        create: 1,
        read: 1,
        update: 1,
        delete: 1,
      });
      return;
    }

    const foundPermission = rolePermissions.find(
      (item) => item.menu?.url === location.pathname
    );

    if (!foundPermission) {
      navigate("/dashboard");
      return;
    }

    setPermission({
      create: foundPermission.create,
      read: foundPermission.read,
      update: foundPermission.update,
      delete: foundPermission.delete,
    });
  }, [location.pathname, navigate, storageLevel]);

  const getRows = (responseData) => {
    if (Array.isArray(responseData?.data?.data)) return responseData.data.data;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.result)) return responseData.result;
    if (Array.isArray(responseData)) return responseData;
    return [];
  };

  const isActive = (item) =>
    Number(item?.status) === 1 ||
    item?.is_active === true ||
    Number(item?.is_active) === 1;

  const fetchInitialMaster = useCallback(async () => {
    setLoading(true);

    try {
      const semesterResponse = await APIMS.get("/api/access/semesters", {
        ...fetchParams,
        params: {
          page: 1,
          limit: 1000,
          ascending: 0,
          search: "",
          level: storageLevel,
        },
      });

      const semesters = getRows(semesterResponse?.data).sort((first, second) => {
        const yearComparison = String(second?.name_year || "").localeCompare(
          String(first?.name_year || ""),
          "id",
          { numeric: true, sensitivity: "base" }
        );

        if (yearComparison !== 0) return yearComparison;
        return Number(first?.semester || 0) - Number(second?.semester || 0);
      });

      setSemesterOptions(semesters);

      const activeSemester = semesters.find(isActive) || semesters[0];
      const activeAcademicYearId =
        activeSemester?.ysb_academic_year_id || "";

      setActiveAcademicYear({
        id: activeAcademicYearId,
        name:
          activeSemester?.name_year ||
          activeSemester?.academic_year ||
          "",
        activeSemesterId: activeSemester?.id || "",
      });

      setFormFilter({
        ysb_academic_year_id: activeAcademicYearId,
        ysb_semester_id: activeSemester?.id || "",
      });
    } catch (error) {
      setSemesterOptions([]);
      setActiveAcademicYear({
        id: "",
        name: "",
        activeSemesterId: "",
      });

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal memuat data tahun ajaran / semester",
      });
    } finally {
      setLoading(false);
    }
  }, [fetchParams, storageLevel]);

  useEffect(() => {
    fetchInitialMaster();
  }, [fetchInitialMaster]);

  const GetResponseData = useCallback(async (overrideFilter = null) => {
    const appliedFilter = overrideFilter || formFilter;

    if (
      !appliedFilter.ysb_academic_year_id ||
      !appliedFilter.ysb_semester_id
    ) {
      return;
    }

    setIsFetching(true);

    try {
      const response = await APILA.get("/api/categories-penilai", {
        ...fetchParams,
        params: {
          page: 1,
          limit: 1000,
          ascending: 1,
          search: "",
          ysb_academic_year_id: appliedFilter.ysb_academic_year_id,
          ysb_semester_id: appliedFilter.ysb_semester_id,
        },
      });

      const rows = getRows(response?.data);

      setData(rows);
      setHasSearched(true);
    } catch (error) {
      setData([]);
      setHasSearched(true);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal memuat data kategori penilai",
      });
    } finally {
      setIsFetching(false);
    }
  }, [fetchParams, formFilter.ysb_academic_year_id, formFilter.ysb_semester_id]);

  useEffect(() => {
    if (
      firstLoadDone.current ||
      !formFilter.ysb_academic_year_id ||
      !formFilter.ysb_semester_id
    ) {
      return;
    }

    firstLoadDone.current = true;
    GetResponseData();
  }, [
    formFilter.ysb_academic_year_id,
    formFilter.ysb_semester_id,
    GetResponseData,
  ]);

  const handleSemesterChange = (event) => {
    const semesterId = event.target.value;
    const selectedSemester = semesterOptions.find(
      (item) => String(item?.id) === String(semesterId)
    );

    setFormFilter({
      ysb_academic_year_id:
        selectedSemester?.ysb_academic_year_id || "",
      ysb_semester_id: semesterId,
    });

    setSearchKeyword("");
    setData([]);
    setHasSearched(false);
    setTablePage(1);
  };

  const handleCari = async () => {
    if (
      !formFilter.ysb_academic_year_id ||
      !formFilter.ysb_semester_id
    ) {
      swal({
        title: "Filter belum lengkap",
        text: "Silakan pilih tahun ajaran / semester terlebih dahulu.",
        icon: "warning",
      });
      return;
    }

    setSearchKeyword("");
    setData([]);
    setHasSearched(false);

    await GetResponseData();
  };

  const handleSync = async () => {
    const { ysb_academic_year_id, ysb_semester_id } = formFilter;
    if (!ysb_academic_year_id || !ysb_semester_id) {
      ToastError.fire({ icon: "error", title: "Pilih tahun ajaran dan semester terlebih dahulu" });
      return;
    }

    const selectedSemester = semesterOptions.find(
      (item) => String(item?.id) === String(ysb_semester_id)
    );
    const selectedLabel = `${selectedSemester?.name_year || selectedSemester?.academic_year || "Tahun ajaran"}${selectedSemester?.semester ? ` - Semester ${selectedSemester.semester}` : ""}`;

    const confirmation = await Swal.fire({
      customClass: {
        popup: "kategori-penilai-confirm-popup",
        title: "kategori-penilai-confirm-title",
        htmlContainer: "kategori-penilai-confirm-text",
        actions: "kategori-penilai-confirm-actions",
      },
      title: "Duplikat Data Kategori Penilai?",
      text: `Apakah Anda ingin menduplikat data dari semester sebelumnya ke ${selectedLabel}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, Duplikat Data",
      cancelButtonText: "Batal",
      confirmButtonColor: "#385deb",
      reverseButtons: true,
    });
    if (!confirmation.isConfirmed) return;

    // Ambil ID pengguna yang sedang login, bukan ID semester atau tahun ajaran.
    let storedUser = {};
    try {
      const userJson = localStorage.getItem("user") || localStorage.getItem("user_data");
      if (userJson) storedUser = JSON.parse(userJson) || {};
    } catch (_) { /* Abaikan data pengguna yang bukan JSON. */ }
    // const userId =
    //   localStorage.getItem("user_id") ||
    //   localStorage.getItem("id_user") ||
    //   storedUser?.id ||
    //   storedUser?.user_id ||
    //   storedUser?.data?.id;

    const userId = localStorage.getItem("id_admin")?.replace(/[|&;$%@"<>()+,]/g, "");

    if (!userId) {
      ToastError.fire({ icon: "error", title: "ID pengguna tidak ditemukan. Periksa data login." });
      return;
    }

    setIsSyncing(true);
    try {
      const response = await APILA.post(
        "/api/categories-penilai/sync",
        { ysb_academic_year_id, ysb_semester_id, user_id: userId },
        fetchParams
      );
      if (response?.data?.error || response?.data?.success === false) {
        throw new Error(response?.data?.message || "Gagal menduplikat data");
      }
      ToastSuccess.fire({
        icon: "success",
        title: response?.data?.message || "Data kategori penilai berhasil diduplikat",
      });
      setSearchKeyword("");
      setTablePage(1);
      await GetResponseData({ ysb_academic_year_id, ysb_semester_id });
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title: error?.response?.data?.message || error?.message || "Gagal menduplikat data kategori penilai",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const nameCollator = useMemo(
    () =>
      new Intl.Collator("id", {
        sensitivity: "base",
        numeric: true,
      }),
    []
  );

  const filteredData = useMemo(() => {
    const query = searchKeyword.trim().toLowerCase();
    const source = Array.isArray(data) ? data : [];

    const filtered = query
      ? source.filter((item) => {
          const nama = String(item?.nama || "").toLowerCase();
          const bobot = String(item?.bobot ?? "").toLowerCase();

          return nama.includes(query) || bobot.includes(query);
        })
      : source;

    return [...filtered].sort((first, second) =>
      nameCollator.compare(
        String(first?.nama || "").trim(),
        String(second?.nama || "").trim()
      )
    );
  }, [data, nameCollator, searchKeyword]);

  const totalRows = filteredData.length;

  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalRows / Math.max(1, rowsPerPage));
    return pages <= 0 ? 1 : pages;
  }, [rowsPerPage, totalRows]);

  useEffect(() => {
    setTablePage(1);
  }, [data, searchKeyword]);

  useEffect(() => {
    if (tablePage > totalPages) {
      setTablePage(totalPages);
    }

    if (tablePage < 1) {
      setTablePage(1);
    }
  }, [tablePage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (tablePage - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, rowsPerPage, tablePage]);

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    const pages = [];

    if (totalPages <= maxButtons) {
      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
        pages.push(pageNumber);
      }
      return pages;
    }

    const left = Math.max(1, tablePage - 1);
    const right = Math.min(totalPages, tablePage + 1);

    pages.push(1);

    if (left > 2) {
      pages.push("...");
    }

    for (let pageNumber = left; pageNumber <= right; pageNumber += 1) {
      if (pageNumber !== 1 && pageNumber !== totalPages) {
        pages.push(pageNumber);
      }
    }

    if (right < totalPages - 1) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages.filter(
      (value, index, array) => array.indexOf(value) === index
    );
  }, [tablePage, totalPages]);

  const goToPage = (nextPage) => {
    const destination = Math.min(
      Math.max(1, Number(nextPage) || 1),
      totalPages
    );

    setTablePage(destination);
  };

  const handleAddSaved = async (savedFilter) => {
    const nextFilter = {
      ysb_academic_year_id:
        savedFilter?.ysb_academic_year_id || activeAcademicYear.id || "",
      ysb_semester_id:
        savedFilter?.ysb_semester_id || activeAcademicYear.activeSemesterId || "",
    };

    setFormFilter(nextFilter);
    setSearchKeyword("");
    setTablePage(1);

    await GetResponseData(nextFilter);
  };

  const deleteById = (item) => {
    const id = item?.id;

    if (!id) {
      ToastError.fire({
        icon: "error",
        title: "ID kategori penilai tidak ditemukan",
      });
      return;
    }

    Swal.fire({
      title: "Apakah kamu yakin?",
      text: `Kategori "${item?.nama || "-"}" akan dihapus.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, Hapus",
      cancelButtonText: "Batal",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),

      preConfirm: async () => {
        try {
          const response = await APILA.delete(
            `/api/categories-penilai/${id}`,
            fetchParams
          );

          if (response?.data?.error) {
            throw new Error(response?.data?.message || "Gagal menghapus data");
          }

          return response;
        } catch (error) {
          Swal.showValidationMessage(
            error?.response?.data?.message ||
              error?.message ||
              "Gagal menghapus data kategori penilai"
          );

          return false;
        }
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      ToastSuccess.fire({
        icon: "success",
        title: "Kategori penilai berhasil dihapus",
      });

      GetResponseData();
    });
  };

  const openUpdateModal = (item) => {
    setSelectedId(item?.id || null);
    setSelectedData(item || null);
    setModalUpdate(true);
  };

  return (
    <div className="body module-page">
      <style>{pageStyles}</style>

      {modalAdd && (
        <ModalAdd
          show={modalAdd}
          onHide={() => setModalAdd(false)}
          activeAcademicYear={activeAcademicYear}
          semesterOptions={semesterOptions}
          onSaved={handleAddSaved}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          show={modalUpdate}
          onHide={() => setModalUpdate(false)}
          GetResponseData={GetResponseData}
          id={selectedId}
          dataUpdate={selectedData}
          activeAcademicYear={activeAcademicYear}
          currentSemesterId={formFilter.ysb_semester_id}
          semesterOptions={semesterOptions}
          onSaved={handleAddSaved}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faBookOpen} />
            </span>
            <h4 className="page-title-text">Kategori Penilai</h4>
          </div>

          <div className="d-flex">
            {Number(permission.create) === 1 && (
              <button
                type="button"
                className="btn btn-create module-header-button"
                onClick={() => setModalAdd(true)}
                disabled={
                  !activeAcademicYear.id ||
                  !activeAcademicYear.activeSemesterId
                }
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Tambah Kategori Penilai</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="body-content module-content">
        <div className="body-toolbar-card kategori-penilai-toolbar">
          <div className="body-filter-side">
            {loading ? (
              <>
                <div className="filter-shimmer body-skeleton-select" />
                <div className="filter-shimmer body-skeleton-button" />
              </>
            ) : (
              <>
                <div className="body-filter-field">
                  <label className="body-filter-label">
                    Tahun Ajaran / Semester
                  </label>
                  <select
                    className="body-filter-select"
                    name="ysb_semester_id"
                    value={formFilter.ysb_semester_id}
                    onChange={handleSemesterChange}
                  >
                    <option value="">Pilih Tahun Ajaran / Semester</option>
                    {semesterOptions.map((item) => (
                      <option key={item?.id} value={item?.id}>
                        {item?.name_year ||
                          item?.academic_year ||
                          "Tahun Ajaran"}
                        {item?.semester
                          ? ` (Semester ${item.semester})`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  className="body-filter-button"
                  onClick={handleCari}
                  disabled={
                    !formFilter.ysb_academic_year_id ||
                    !formFilter.ysb_semester_id ||
                    isFetching
                  }
                >
                  Tampilkan Data
                </button>
              </>
            )}
          </div>
          {Number(permission.create) === 1 && (
            <div className="kategori-penilai-sync-wrap">
              <button
                type="button"
                className="kategori-penilai-sync-button"
                onClick={handleSync}
                disabled={loading || isFetching || isSyncing || !formFilter.ysb_academic_year_id || !formFilter.ysb_semester_id}
              >
                <FontAwesomeIcon icon={faCopy} />
                <span>{isSyncing ? "Menduplikat..." : "Duplikat Data"}</span>
              </button>
            </div>
          )}
        </div>

        {isFetching && (
          <div className="module-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        )}

        {hasSearched && !isFetching && (
          <>
            <div className="module-search-wrap body-search-wrap">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="module-search-icon"
              />
              <input
                type="text"
                className="module-search-input"
                placeholder="Cari nama kategori atau bobot..."
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
              />
            </div>

            <div className="module-table-card">
              <div className="module-table-scroll">
                <table className="module-table card-table kategori-penilai-table">
                  <thead>
                    <tr>
                      <th className="module-col-number">No</th>
                      <th>Kategori Penilai</th>
                      <th className="kategori-penilai-col-bobot">Bobot</th>
                      <th className="kategori-penilai-col-action">Aksi</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="module-empty-state">
                          Data kategori penilai tidak ditemukan
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item, index) => (
                        <tr key={item?.id || `kategori-${index}`}>
                          <td className="module-col-number">
                            <span className="module-number-badge">
                              {(tablePage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>

                          <td>
                            <span className="module-name-text">
                              {item?.nama || "-"}
                            </span>
                          </td>

                          <td className="kategori-penilai-col-bobot">
                            <span className="module-name-text">
                              {item?.bobot ?? "-"}
                            </span>
                          </td>

                          <td className="kategori-penilai-col-action">
                            <div className="module-action-wrap kategori-penilai-action-wrap">
                              {Number(permission.update) === 1 && (
                                <button
                                  type="button"
                                  className="module-action-button module-action-edit"
                                  title="Ubah kategori penilai"
                                  onClick={() => openUpdateModal(item)}
                                >
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                              )}

                              {Number(permission.delete) === 1 && (
                                <button
                                  type="button"
                                  className="module-action-button module-action-delete"
                                  title="Hapus kategori penilai"
                                  onClick={() => deleteById(item)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}

                              {Number(permission.update) !== 1 &&
                                Number(permission.delete) !== 1 && (
                                  <span className="module-muted-text">-</span>
                                )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filteredData.length > 0 && (
                <div className="module-pagination">
                  <div className="module-pagination-left">
                    <span className="module-page-info">
                      Menampilkan{" "}
                      <b>
                        {Math.min(
                          (tablePage - 1) * rowsPerPage + 1,
                          totalRows
                        )}
                      </b>
                      -<b>{Math.min(tablePage * rowsPerPage, totalRows)}</b> dari{" "}
                      <b>{totalRows}</b> data
                    </span>

                    <select
                      className="module-page-size"
                      value={rowsPerPage}
                      onChange={(event) => {
                        setRowsPerPage(Number(event.target.value) || 10);
                        setTablePage(1);
                      }}
                    >
                      <option value={10}>10 / halaman</option>
                      <option value={25}>25 / halaman</option>
                      <option value={50}>50 / halaman</option>
                      <option value={100}>100 / halaman</option>
                    </select>
                  </div>

                  <div className="module-pagination-right">
                    <button
                      type="button"
                      className="module-page-nav"
                      onClick={() => goToPage(tablePage - 1)}
                      disabled={tablePage <= 1}
                    >
                      Prev
                    </button>

                    <div className="module-page-numbers">
                      {pageNumbers.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="module-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            type="button"
                            key={`page-${page}`}
                            className={`module-page-number ${
                              page === tablePage ? "active" : ""
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      type="button"
                      className="module-page-nav"
                      onClick={() => goToPage(tablePage + 1)}
                      disabled={tablePage >= totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
